import type { Member } from '@/types/member';

export interface AddressParts {
  address?: string | null;
  numar?: string | null;
  bloc?: string | null;
  scara?: string | null;
  etaj?: string | null;
  apartament?: string | null;
  city?: string | null;
}

/**
 * Build a single-line address, e.g. "Strada X nr. 5, bl. B2 sc. A et. 3 ap. 12, Arad".
 * Returns '' when no parts are present.
 */
export function formatAddress(p: AddressParts, opts: { includeCity?: boolean } = {}): string {
  const { includeCity = true } = opts;
  const parts: string[] = [];

  if (p.address) {
    parts.push(p.numar ? `${p.address} nr. ${p.numar}` : p.address);
  } else if (p.numar) {
    parts.push(`nr. ${p.numar}`);
  }

  const struct = [
    p.bloc && `bl. ${p.bloc}`,
    p.scara && `sc. ${p.scara}`,
    p.etaj && `et. ${p.etaj}`,
    p.apartament && `ap. ${p.apartament}`,
  ].filter(Boolean);
  if (struct.length) parts.push(struct.join(' '));

  if (includeCity && p.city) parts.push(p.city);

  return parts.join(', ');
}

/**
 * Build a Google Maps link. Prefers the exact saved coordinates so the external
 * link matches the manually-corrected pin in the app; falls back to the text
 * address only when no coordinates are available.
 */
export function googleMapsUrl(opts: {
  latitude?: number | null;
  longitude?: number | null;
  text?: string | null;
}): string {
  const { latitude, longitude, text } = opts;
  const query =
    latitude != null && longitude != null ? `${latitude},${longitude}` : text ?? '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * Format a member's effective address (the family's when in a family, else
 * the member's own) from the `effective_*` fields returned by the API.
 */
export function formatEffectiveAddress(
  m: Pick<
    Member,
    | 'effective_address'
    | 'effective_numar'
    | 'effective_bloc'
    | 'effective_scara'
    | 'effective_etaj'
    | 'effective_apartament'
    | 'effective_city'
  >,
  opts?: { includeCity?: boolean },
): string {
  return formatAddress(
    {
      address: m.effective_address,
      numar: m.effective_numar,
      bloc: m.effective_bloc,
      scara: m.effective_scara,
      etaj: m.effective_etaj,
      apartament: m.effective_apartament,
      city: m.effective_city,
    },
    opts,
  );
}
